import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { referFriendStoreKey } from './referFriend.const';
import {
  declineReferral,
  submitReferral,
  loginReferrer,
  fetchReferrerProfile,
  fetchReferrerCountryOptions,
} from './referFriend.thunks';
import { IReferrer } from '../../types/referFriend';
import { API_STATUS } from 'src/constants';
import { ICountry } from 'src/types';

export interface IReferFriendSliceState {
  referrer: {
    data: IReferrer | null;
    apiStatus: API_STATUS;
  };
  countries: {
    data: ICountry[];
    apiStatus: API_STATUS;
  };
  referralName: string | null;
  authEmailSentTo: string | null;
  isPerformingAction: boolean;
  wasRejected: boolean;
  inProcess: boolean;
}

const initialState: IReferFriendSliceState = {
  referrer: {
    data: null,
    apiStatus: API_STATUS.IDLE,
  },
  countries: {
    data: [],
    apiStatus: API_STATUS.IDLE,
  },
  referralName: null,
  authEmailSentTo: null,
  isPerformingAction: false,
  wasRejected: false,
  inProcess: false,
};

export const referFriendSlice = createSlice({
  name: referFriendStoreKey,
  initialState,
  reducers: {
    resetAuthEmailSentTo: (state) => {
      state.authEmailSentTo = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitReferral.pending, (state) => {
        state.isPerformingAction = true;
      })
      .addCase(submitReferral.fulfilled, (state, action) => {
        state.isPerformingAction = false;
        state.wasRejected = action.payload.wasRejected;
        state.inProcess = action.payload.inProcess ?? false;
        state.referralName = action.meta.arg.name ?? null;
      })
      .addCase(submitReferral.rejected, (state, action: PayloadAction<any>) => {
        state.isPerformingAction = false;

        if (action?.payload?.statusCode === 401) {
          state.referrer.apiStatus = API_STATUS.COMPLETE;
          state.referrer.data = null;
        }
      })
      .addCase(declineReferral.pending, (state) => {
        state.isPerformingAction = true;
      })
      .addCase(declineReferral.fulfilled, (state) => {
        state.isPerformingAction = false;
      })
      .addCase(declineReferral.rejected, (state) => {
        state.isPerformingAction = false;
      })
      .addCase(loginReferrer.pending, (state) => {
        state.isPerformingAction = true;
      })
      .addCase(loginReferrer.fulfilled, (state, action) => {
        state.isPerformingAction = false;
        if (action.payload.isEmailSent) {
          state.authEmailSentTo = action.meta.arg.email ?? null;
        }
      })
      .addCase(loginReferrer.rejected, (state) => {
        state.isPerformingAction = false;
      })
      .addCase(fetchReferrerProfile.pending, (state) => {
        state.referrer.apiStatus = API_STATUS.LOADING;
      })
      .addCase(fetchReferrerProfile.fulfilled, (state, action) => {
        state.referrer.apiStatus = API_STATUS.COMPLETE;
        state.referrer.data = action.payload;
      })
      .addCase(
        fetchReferrerProfile.rejected,
        (state, action: PayloadAction<any>) => {
          state.referrer.apiStatus = API_STATUS.FAILED;

          if (action?.payload?.statusCode === 401) {
            state.referrer.apiStatus = API_STATUS.COMPLETE;
            state.referrer.data = null;
          }
        },
      )
      .addCase(fetchReferrerCountryOptions.pending, (state) => {
        state.countries.apiStatus = API_STATUS.LOADING;
      })
      .addCase(fetchReferrerCountryOptions.fulfilled, (state, action) => {
        state.countries.apiStatus = API_STATUS.COMPLETE;
        state.countries.data = action.payload;
      })
      .addCase(fetchReferrerCountryOptions.rejected, (state) => {
        state.countries.apiStatus = API_STATUS.FAILED;
      });
  },
});

export const { resetAuthEmailSentTo } = referFriendSlice.actions;
